import { useCallback } from 'react';
import PropTypes from 'prop-types';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import { compose, includes, isEmpty, times } from 'ramda';
import { useDrop } from 'react-dnd-cjs';
import { connect } from 'react-redux';

import { getLabelMessage } from '@gef-ui/localization/messages';

import { CardDropField, codelistProvider } from 'components';
import SectionHeader from 'components/atoms/GefMui/SectionHeader';
import { codelists } from 'constants/codelists';
import { dragItems } from 'constants/general';
import campaignDetail from 'features/views/campaignSettings/campaignDetail';
import { MAX_CONTACT_DATA_COLUMNS } from 'features/views/campaignSettings/campaignDetail/constants';

import DraggableColumn from './DraggableColumn';

const containerId = 'campaign-detail';

const getColumn = (campaignDataColumns) => (columnId) => {
	return campaignDataColumns.find((dataColumn) => dataColumn.id === columnId);
};

const RenderedColumns = (props) => {
	const { selectedColumns, campaignDataColumns, addColumn, removeColumn } = props;

	const columns = isEmpty(campaignDataColumns) ? [] : selectedColumns.map(getColumn(campaignDataColumns));

	const handleCardRemove = useCallback(
		(id) => {
			removeColumn(id);
		},
		[removeColumn],
	);

	const [{ canDrop }, drop] = useDrop({
		accept: dragItems.CAMPAIGN_DATA_COLUMN,
		canDrop: (item) => containerId !== item.containerId,
		drop: (item) => {
			if (containerId !== item.containerId) {
				if (!includes(item.payload.id, selectedColumns)) {
					addColumn(item.payload.id);
				}
			}
		},
		collect: (monitor) => ({
			canDrop: monitor.canDrop(),
		}),
	});

	return (
		<Paper sx={{ p: 3, borderRadius: 2 }}>
			<SectionHeader sx={{ mb: 2 }} text={getLabelMessage('campaign:renderColumnsTitle')} />
			<Grid container>
				<Grid size={12}>
					<div ref={drop}>
						<Grid container spacing={3}>
							{columns.map((column) => {
								return (
									<Grid key={column.id} size={4}>
										<DraggableColumn
											containerId={containerId}
											column={column}
											onRemove={handleCardRemove}
										/>
									</Grid>
								);
							})}
							{times(
								(i) => (
									<Grid key={i} size={4}>
										<CardDropField
											animate={canDrop && i === 0}
											dropFieldText={getLabelMessage('campaignState:dropText')}
										/>
									</Grid>
								),
								MAX_CONTACT_DATA_COLUMNS - selectedColumns.length,
							)}
						</Grid>
					</div>
				</Grid>
			</Grid>
		</Paper>
	);
};

RenderedColumns.propTypes = {
	addColumn: PropTypes.func,
	campaignDataColumns: PropTypes.arrayOf(PropTypes.object),
	removeColumn: PropTypes.func,
	selectedColumns: PropTypes.arrayOf(PropTypes.string),
};

export default compose(
	codelistProvider(codelists.campaignDataColumns, 'campaignDataColumns'),
	connect(
		(state) => ({
			selectedColumns: campaignDetail.selectors.getColumns(state),
		}),
		{
			removeColumn: campaignDetail.actions.removeContactDataColumn,
			addColumn: campaignDetail.actions.addContactDataColumnWithMaxItemsCheck,
		},
	),
)(RenderedColumns);
